import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import {
    TURNSTILE_SITEKEY,
    isTurnstileEnabled,
    setTurnstileToken,
    registerTurnstileReset,
} from '../../../../services/turnstile';

/**
 * Cloudflare Turnstile widget.
 *
 * EXPLICIT RENDER, NOT AUTO-RENDER. Turnstile's script scans the DOM for `.cf-turnstile`
 * elements when it loads. In this SPA the auth forms mount long after that scan has run,
 * so an auto-rendered widget would simply never appear. window.turnstile.render() is the
 * only reliable path here.
 *
 * The script may also not be parsed yet when this mounts, so readiness is polled briefly
 * rather than assumed.
 *
 * ACCESSIBILITY: in invisible mode nothing is shown and nothing is focusable. When
 * Cloudflare decides a visitor must interact, the widget becomes a real interactive
 * element — do not hide this container with display:none or CSS that removes it from the
 * accessibility tree, or a flagged user is left with an unsolvable, invisible challenge
 * and no way to log in.
 */
const TurnstileWidget = ({ action = 'turnstile-spin-v2', onSolved }) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useEffect(() => {
        if (!isTurnstileEnabled() || !containerRef.current) {
            return undefined;
        }

        let cancelled = false;
        let pollTimer = null;

        const renderWidget = () => {
            if (cancelled || !containerRef.current) {
                return;
            }
            if (!window.turnstile) {
                // Script not parsed yet. Poll rather than give up — a slow network on
                // first load would otherwise permanently leave the form without a token,
                // and the backend fails closed, so the user could never log in.
                pollTimer = setTimeout(renderWidget, 150);
                return;
            }

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: TURNSTILE_SITEKEY,
                action,
                callback: (token) => {
                    setTurnstileToken(token);
                    if (onSolved) {
                        onSolved(token);
                    }
                },
                'expired-callback': () => {
                    // Tokens expire (~5 min). Clear and re-issue so a user who left the
                    // form open does not submit a stale token.
                    setTurnstileToken('');
                    if (widgetIdRef.current !== null) {
                        window.turnstile.reset(widgetIdRef.current);
                    }
                },
                'error-callback': () => {
                    // Network/challenge error. Clear the token so nothing stale is sent;
                    // the backend fails closed and the user gets a retryable error.
                    setTurnstileToken('');
                },
            });

            registerTurnstileReset(() => {
                if (window.turnstile && widgetIdRef.current !== null) {
                    window.turnstile.reset(widgetIdRef.current);
                }
            });
        };

        renderWidget();

        return () => {
            cancelled = true;
            if (pollTimer) {
                clearTimeout(pollTimer);
            }
            registerTurnstileReset(null);
            setTurnstileToken('');
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [action, onSolved]);

    if (!isTurnstileEnabled()) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <div
                ref={containerRef}
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITEKEY}
                data-action={action}
            />
        </Box>
    );
};

export default TurnstileWidget;
